import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getBanners } from "../lib/api";

const DEFAULT_BANNERS = [
    {
        id: "default-1",
        title: "Solární panely & FVE systémy",
        subtitle: "Kompletní řešení pro váš dům nebo firmu. Instalace po celé ČR.",
        bg_color: "#1a4e1c",
        text_color: "#ffffff",
        button_text: "Prozkoumat nabídku",
        link: "/kategorie",
        badge: "Akce",
        badge_color: "#e53e3e",
    },
    {
        id: "default-2",
        title: "Bateriové systémy pro váš domov",
        subtitle: "Ukládejte přebytky energie a šetřete na elektřině celoroční.",
        bg_color: "#0d3d5a",
        text_color: "#ffffff",
        button_text: "Zobrazit baterie",
        link: "/kategorie/baterie",
        badge: "Novinka",
        badge_color: "#389622",
    },
    {
        id: "default-3",
        title: "Loxone Smart Home",
        subtitle: "Chytrá automatizace vašeho domu – světla, rolety, topení.",
        bg_color: "#2d5a1b",
        text_color: "#ffffff",
        button_text: "Více o Loxone",
        link: "/loxone",
        badge: null,
    },
];

export default function BannerSlider() {
    const [banners, setBanners] = useState([]);
    const [current, setCurrent] = useState(0);
    const [autoplay, setAutoplay] = useState(true);

    useEffect(() => {
        getBanners("homepage")
            .then(r => {
                if (r.data && r.data.length > 0) setBanners(r.data);
                else setBanners(DEFAULT_BANNERS);
            })
            .catch(() => setBanners(DEFAULT_BANNERS));
    }, []);

    const next = useCallback(() => setCurrent(c => (c + 1) % banners.length), [banners.length]);
    const prev = useCallback(() => setCurrent(c => (c - 1 + banners.length) % banners.length), [banners.length]);

    useEffect(() => {
        if (!autoplay || banners.length <= 1) return;
        const timer = setInterval(next, 5000);
        return () => clearInterval(timer);
    }, [autoplay, banners.length, next]);

    if (banners.length === 0) return null;

    const banner = banners[current];

    return (
        <div className="relative rounded-2xl overflow-hidden shadow-lg"
            style={{ minHeight: 220, backgroundColor: banner.bg_color || "#1a4e1c" }}
            onMouseEnter={() => setAutoplay(false)}
            onMouseLeave={() => setAutoplay(true)}>

            {/* Background image if any */}
            {banner.image_url && (
                <div className="absolute inset-0">
                    <img src={banner.image_url} alt="" className="w-full h-full object-cover opacity-25" />
                </div>
            )}

            {/* Content */}
            <div className="relative z-10 flex flex-col justify-center h-full px-8 py-10 md:py-12 max-w-2xl">
                {banner.badge && (
                    <span className="inline-flex text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit"
                        style={{ backgroundColor: banner.badge_color || "#e53e3e" }}>
                        {banner.badge}
                    </span>
                )}
                <h2 className="text-2xl md:text-3xl font-bold mb-2 leading-tight"
                    style={{ color: banner.text_color || "#fff" }}>
                    {banner.title}
                </h2>
                {banner.subtitle && (
                    <p className="text-sm md:text-base mb-5 opacity-85"
                        style={{ color: banner.text_color || "#fff" }}>
                        {banner.subtitle}
                    </p>
                )}
                {banner.link && (
                    <Link to={banner.link}
                        className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full bg-white hover:bg-gray-100 transition-colors w-fit"
                        style={{ color: banner.bg_color || "#1a4e1c" }}>
                        {banner.button_text || "Více info"}
                    </Link>
                )}
            </div>

            {/* Navigation */}
            {banners.length > 1 && (
                <>
                    <button onClick={prev}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition-colors z-20">
                        <ChevronLeft size={18} />
                    </button>
                    <button onClick={next}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/20 hover:bg-white/40 text-white flex items-center justify-center transition-colors z-20">
                        <ChevronRight size={18} />
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
                        {banners.map((_, i) => (
                            <button key={i} onClick={() => setCurrent(i)}
                                className={`h-1.5 rounded-full transition-all ${i === current ? "w-6 bg-white" : "w-1.5 bg-white/40"}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
